import { AfterViewInit, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ColumnData } from 'wecheer-common';

export interface ChangelogHistoryDialogData {
  changes: any[];
  columns: ColumnData[];
  receiptId?: string;
  campaignTitle?: string;
}

@Component({
  selector: 'app-changelog-history-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule
],
  templateUrl: './changelog-history-dialog.component.html',
  styleUrls: ['./changelog-history-dialog.component.scss']
})
export class ChangelogHistoryDialogComponent implements AfterViewInit {
  constructor(
    public dialogRef: MatDialogRef<ChangelogHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangelogHistoryDialogData
  ) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.dialogRef.updateSize(undefined, 'auto');
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  isFromColumn(columnKey: string): boolean {
    return columnKey === 'from';
  }

  isToColumn(columnKey: string): boolean {
    return columnKey === 'to';
  }

  getDisplayValue(value: unknown): string {
    const fullValue = this.getFullValue(value);
    return fullValue || '-';
  }

  getFullValue(value: unknown): string {
    return this.normalizeChangeValue(value);
  }

  getChangedFromValue(change: Record<string, unknown>): string {
    return this.extractDifference(
      this.normalizeChangeValue(change?.['from']),
      this.normalizeChangeValue(change?.['to'])
    );
  }

  getChangedToValue(change: Record<string, unknown>): string {
    return this.extractDifference(
      this.normalizeChangeValue(change?.['to']),
      this.normalizeChangeValue(change?.['from'])
    );
  }

  private normalizeChangeValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = typeof value === 'string' ? value : String(value);
    const trimmed = stringValue.trim();

    if (!trimmed || trimmed.toLowerCase() === 'n/a') {
      return '';
    }

    return trimmed;
  }

  // Returns only the portion of the source string that differs from the compare string.
  private extractDifference(source: string, compare: string): string {
    if (!source) {
      return '';
    }

    if (!compare) {
      return source;
    }

    if (source === compare) {
      return '';
    }

    const prefixLength = this.getCommonPrefixLength(source, compare);
    const suffixLength = this.getCommonSuffixLength(source, compare, prefixLength);
    const endIndex = source.length - suffixLength;
    const changedSegment = source.substring(prefixLength, Math.max(prefixLength, endIndex));
    const trimmedSegment = changedSegment.trim();

    if (trimmedSegment) {
      return trimmedSegment;
    }

    return changedSegment;
  }

  private getCommonPrefixLength(a: string, b: string): number {
    let index = 0;
    const max = Math.min(a.length, b.length);

    while (index < max && a.charCodeAt(index) === b.charCodeAt(index)) {
      index++;
    }

    return index;
  }

  private getCommonSuffixLength(a: string, b: string, prefixLength: number): number {
    let index = 0;
    const aLimit = a.length - prefixLength;
    const bLimit = b.length - prefixLength;

    while (
      index < aLimit &&
      index < bLimit &&
      a.charCodeAt(a.length - 1 - index) === b.charCodeAt(b.length - 1 - index)
    ) {
      index++;
    }

    return index;
  }
}
